// utils/api.ts
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { getToken } from '../tokenUtils/tokenUtils';

// Create a single axios instance
const apiInstance: AxiosInstance = axios.create({
  baseURL: process.env.GATSBY_PMS_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Single-flight guard so concurrent 401s only refresh once
let refreshingPromise: Promise<string> | null = null;
function refreshOnce(): Promise<string> {
  if (!refreshingPromise) {
    refreshingPromise = getToken(true).finally(() => {
      refreshingPromise = null;
    });
  }
  return refreshingPromise;
}

// Attach the latest token to every request (per-request header, no global mutation)
apiInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Respect any Authorization explicitly provided by the caller
    const hasAuth = !!config.headers?.Authorization;
    if (!hasAuth) {
      const token = await getToken(); // should already buffer near-expiry in your token utils
      config.headers = {
        ...(config.headers || {}),
        Authorization: `Bearer ${token}`,
      };
    }
    return config;
  }
);

// On 401, refresh token once and retry the original request
apiInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

    if (status === 401 && original && !original._retry) {
      original._retry = true;
      try {
        const newToken = await refreshOnce();
        original.headers = {
          ...(original.headers || {}),
          Authorization: `Bearer ${newToken}`,
        };
        // Retry with the same instance (keeps baseURL, interceptors, etc.)
        return apiInstance.request(original);
      } catch (e) {
        // refresh failed — fall through to rejection
      }
    }

    // Enhanced logging
    if (axios.isAxiosError(error)) {
      const cfg = error.config as InternalAxiosRequestConfig | undefined;
      console.error('Request failed:', {
        url: cfg?.baseURL ? `${cfg.baseURL}${cfg.url ?? ''}` : cfg?.url,
        method: cfg?.method,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        wwwAuthenticate: error.response?.headers?.['www-authenticate'],
      });
    }
    return Promise.reject(error);
  }
);

// Thin wrapper to match your current API
const fetchWithToken = async <T = any>(
  url: string,
  options: AxiosRequestConfig = {}
): Promise<AxiosResponse<T>> => {
  // Do not touch apiInstance.defaults.headers here — headers are set per request
  return apiInstance.request<T>({ url, ...options });
};

export default fetchWithToken;
